import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { parseNumberInput, getNumberInputValue } from '../utils/numberInputHelpers';
import { useModalClose } from '../hooks/useModalClose';

export default function OptionManagement({ theme }) {
    const { t } = useLanguage();
    const [optionGroups, setOptionGroups] = useState([]);
    const [options, setOptions] = useState([]);
    const [optionTypes, setOptionTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // UI state
    const [activeView, setActiveView] = useState('options'); // 'groups' or 'options'
    const [showAddGroupForm, setShowAddGroupForm] = useState(false);
    const [showAddOptionForm, setShowAddOptionForm] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [editingOption, setEditingOption] = useState(null);
    const [filterGroupId, setFilterGroupId] = useState('');
    const [searchTermGroups, setSearchTermGroups] = useState('');
    const [searchTermOptions, setSearchTermOptions] = useState('');

    // Bulk assign state
    const [assigningGroup, setAssigningGroup] = useState(null);
    const [allProducts, setAllProducts] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [assignLoading, setAssignLoading] = useState(false);
    const [assignFilterCategory, setAssignFilterCategory] = useState('');

    // Sorting state
    const [groupSortField, setGroupSortField] = useState('ordinal');
    const [groupSortDirection, setGroupSortDirection] = useState('asc');
    const [optionSortField, setOptionSortField] = useState('ordinal');
    const [optionSortDirection, setOptionSortDirection] = useState('asc');

    // Form states
    const [groupFormData, setGroupFormData] = useState({
        option_type_id: '',
        name: '',
        ordinal: '',
        mandatory: false,
        units: ''
    });

    const [optionFormData, setOptionFormData] = useState({
        option_group_id: '',
        name: '',
        ordinal: '',
        quantity: '',
        selected: false,
        price_adjust: ''
    });

    // Helper function to reset group form
    const resetGroupForm = () => {
        setGroupFormData({ option_type_id: '', name: '', ordinal: '', mandatory: false, units: '' });
    };

    // Helper function to reset option form
    const resetOptionForm = () => {
        setOptionFormData({ option_group_id: '', name: '', ordinal: '', quantity: '', selected: false, price_adjust: '' });
    };

    // Close group modal on ESC/back
    useModalClose(() => {
        setShowAddGroupForm(false);
        setEditingGroup(null);
        resetGroupForm();
    }, showAddGroupForm || editingGroup);

    // Close option modal on ESC/back
    useModalClose(() => {
        setShowAddOptionForm(false);
        setEditingOption(null);
        resetOptionForm();
    }, showAddOptionForm || editingOption);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [groupsRes, optionsRes, typesRes] = await Promise.all([
                fetch(`${BACKEND_URL}/api/admin/option_groups`),
                fetch(`${BACKEND_URL}/api/admin/options`),
                fetch(`${BACKEND_URL}/api/option_types`)
            ]);

            if (!groupsRes.ok || !optionsRes.ok || !typesRes.ok) {
                throw new Error('Failed to fetch data');
            }

            const [groupsData, optionsData, typesData] = await Promise.all([
                groupsRes.json(),
                optionsRes.json(),
                typesRes.json()
            ]);

            setOptionGroups(groupsData);
            setOptions(optionsData);
            setOptionTypes(typesData);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Option Group handlers
    const handleAddGroup = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/option_groups`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(groupFormData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to add option group');
            }

            setShowAddGroupForm(false);
            resetGroupForm();
            fetchData();
            alert(t('success') + ': ' + t('optionGroup') + ' ' + t('created'));
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleEditGroup = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/option_groups/${editingGroup.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(groupFormData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update option group');
            }

            setEditingGroup(null);
            resetGroupForm();
            fetchData();
            alert(t('success') + ': ' + t('optionGroup') + ' ' + t('updated'));
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleDeleteGroup = async (groupId) => {
        if (!window.confirm(t('confirmDelete') + ' ' + t('optionGroup') + '?')) return;

        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/option_groups/${groupId}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete option group');
            }

            fetchData();
            alert(t('success') + ': ' + t('optionGroup') + ' ' + t('deleted'));
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    // Bulk assign: open product selector for an option group
    const handleOpenAssign = async (group) => {
        setAssigningGroup(group);
        setAssignLoading(true);
        setAssignFilterCategory('');
        try {
            const [productsRes, categoriesRes, assignedRes] = await Promise.all([
                fetch(`${BACKEND_URL}/api/products`),
                fetch(`${BACKEND_URL}/api/categories`),
                fetch(`${BACKEND_URL}/api/option-groups/${group.id}/products`)
            ]);
            const products = await productsRes.json();
            const categories = await categoriesRes.json();
            const assignedIds = await assignedRes.json();
            setAllProducts(products.filter(p => p.active));
            setAllCategories(categories);
            setSelectedProductIds(assignedIds);
        } catch {
            alert(t('error'));
        } finally {
            setAssignLoading(false);
        }
    };

    const handleSaveAssign = async () => {
        if (!assigningGroup) return;
        setAssignLoading(true);
        try {
            const response = await fetch(`${BACKEND_URL}/api/option-groups/${assigningGroup.id}/assign-products`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productIds: selectedProductIds })
            });
            if (!response.ok) throw new Error('Failed to save');
            setAssigningGroup(null);
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        } finally {
            setAssignLoading(false);
        }
    };

    const toggleProductSelection = (productId) => {
        setSelectedProductIds(prev =>
            prev.includes(productId)
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    const toggleAllInCategory = (categoryId) => {
        const categoryProducts = allProducts.filter(p => p.category_id === categoryId);
        const categoryIds = categoryProducts.map(p => p.id);
        const allSelected = categoryIds.every(id => selectedProductIds.includes(id));
        if (allSelected) {
            setSelectedProductIds(prev => prev.filter(id => !categoryIds.includes(id)));
        } else {
            setSelectedProductIds(prev => [...new Set([...prev, ...categoryIds])]);
        }
    };

    const handleOpenEditGroup = (group) => {
        setGroupFormData({
            option_type_id: group.option_type_id,
            name: group.name,
            ordinal: group.ordinal,
            mandatory: Boolean(group.mandatory),
            units: group.units || ''
        });
        setEditingGroup(group);
    };

    // Option handlers
    const handleAddOption = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/options`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(optionFormData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to add option');
            }

            setShowAddOptionForm(false);
            resetOptionForm();
            fetchData();
            alert(t('success') + ': ' + t('option') + ' ' + t('created'));
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleEditOption = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/options/${editingOption.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(optionFormData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update option');
            }

            setEditingOption(null);
            resetOptionForm();
            fetchData();
            alert(t('success') + ': ' + t('option') + ' ' + t('updated'));
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleDeleteOption = async (optionId) => {
        if (!window.confirm(t('confirmDelete') + ' ' + t('option') + '?')) return;

        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/options/${optionId}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete option');
            }

            fetchData();
            alert(t('success') + ': ' + t('option') + ' ' + t('deleted'));
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleOpenEditOption = (option) => {
        setOptionFormData({
            option_group_id: option.option_group_id,
            name: option.name,
            ordinal: option.ordinal,
            quantity: option.quantity || '',
            selected: Boolean(option.selected),
            price_adjust: option.price_adjust || ''
        });
        setEditingOption(option);
    };

    // Sorting handlers
    const handleGroupSort = (field) => {
        if (groupSortField === field) {
            setGroupSortDirection(groupSortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setGroupSortField(field);
            setGroupSortDirection('asc');
        }
    };

    const handleOptionSort = (field) => {
        if (optionSortField === field) {
            setOptionSortDirection(optionSortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setOptionSortField(field);
            setOptionSortDirection('asc');
        }
    };

    // Sort function
    const sortData = (data, field, direction) => {
        return [...data].sort((a, b) => {
            let aVal = a[field];
            let bVal = b[field];

            // Handle null/undefined values
            if (aVal === null || aVal === undefined) aVal = '';
            if (bVal === null || bVal === undefined) bVal = '';

            // Convert to lowercase for string comparison
            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();

            if (aVal < bVal) return direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    // Filter functions
    const matchesGroupSearch = (group) => {
        if (!searchTermGroups) return true;
        const search = searchTermGroups.toLowerCase();
        const typeName = optionTypes.find(t => t.id === group.option_type_id)?.name || '';
        return [
            group.name,
            typeName,
            group.units
        ].some(field =>
            field && field.toString().toLowerCase().includes(search)
        );
    };

    const matchesOptionSearch = (option) => {
        if (!searchTermOptions) return true;
        const search = searchTermOptions.toLowerCase();
        const groupName = optionGroups.find(g => g.id === option.option_group_id)?.name || '';
        return [
            option.name,
            groupName,
            option.price_adjust?.toString()
        ].some(field =>
            field && field.toString().toLowerCase().includes(search)
        );
    };

    // Sorted and filtered data
    const searchedGroups = optionGroups.filter(matchesGroupSearch);
    const sortedOptionGroups = sortData(searchedGroups, groupSortField, groupSortDirection);

    const filteredOptions = filterGroupId
        ? options.filter(opt => opt.option_group_id === parseInt(filterGroupId))
        : options;

    const searchedOptions = filteredOptions.filter(matchesOptionSearch);
    const sortedOptions = sortData(searchedOptions, optionSortField, optionSortDirection);

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '2rem', color: theme?.colors?.textSecondary || '#666' }}>
                {t('loading')}
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ textAlign: 'center', padding: '2rem', color: theme?.colors?.error || 'red' }}>
                {t('error')}: {error}
            </div>
        );
    }

    return (
        <div style={{
            padding: '1.5rem',
            backgroundColor: theme?.colors?.background || '#f5f5f5',
            color: theme?.colors?.text || '#000'
        }}>
            {/* View Toggle */}
            <div style={{
                display: 'flex',
                gap: '1rem',
                marginBottom: '1.5rem',
                borderBottom: `2px solid ${theme?.colors?.border || '#ddd'}`,
                paddingBottom: '0.5rem'
            }}>
                <button
                    onClick={() => setActiveView('groups')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: activeView === 'groups' ? theme?.colors?.primary || '#1976d2' : 'transparent',
                        color: activeView === 'groups' ? theme?.colors?.surface || '#fff' : theme?.colors?.text || '#000',
                        border: 'none',
                        borderRadius: theme?.borderRadius || 4,
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: activeView === 'groups' ? 'bold' : 'normal'
                    }}
                >
                    {t('optionGroups')}
                </button>
                <button
                    onClick={() => setActiveView('options')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: activeView === 'options' ? theme?.colors?.primary || '#1976d2' : 'transparent',
                        color: activeView === 'options' ? theme?.colors?.surface || '#fff' : theme?.colors?.text || '#000',
                        border: 'none',
                        borderRadius: theme?.borderRadius || 4,
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: activeView === 'options' ? 'bold' : 'normal'
                    }}
                >
                    {t('options')}
                </button>
            </div>

            {/* Option Groups View */}
            {activeView === 'groups' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h3 style={{ margin: 0, color: theme?.colors?.primary || '#1976d2' }}>
                            {t('optionGroups')} ({optionGroups.length})
                        </h3>
                        <button
                            onClick={() => setShowAddGroupForm(true)}
                            style={{
                                padding: '0.75rem 1.5rem',
                                backgroundColor: theme?.colors?.primary || '#1976d2',
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            + {t('add')} {t('optionGroup')}
                        </button>
                    </div>

                    {/* Search Input for Groups */}
                    <div style={{ marginBottom: '1rem' }}>
                        <input
                            type="text"
                            value={searchTermGroups}
                            onChange={(e) => setSearchTermGroups(e.target.value)}
                            placeholder={t('searchOptionGroups')}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                fontSize: '0.95rem',
                                border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                borderRadius: theme?.borderRadius || 4,
                                backgroundColor: theme?.colors?.surface || '#fff',
                                color: theme?.colors?.text || '#000',
                                outline: 'none'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = theme?.colors?.primary || '#1976d2';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = theme?.colors?.border || '#ddd';
                            }}
                        />
                        {searchTermGroups && (
                            <div style={{
                                marginTop: '0.5rem',
                                fontSize: '0.85rem',
                                color: theme?.colors?.textSecondary || '#666'
                            }}>
                                {t('showing')} {sortedOptionGroups.length} {t('of')} {optionGroups.length} {t('optionGroups').toLowerCase()}
                            </div>
                        )}
                    </div>

                    {/* Option Groups Table */}
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            backgroundColor: theme?.colors?.surface || '#fff',
                            borderRadius: theme?.borderRadius || 8,
                            overflow: 'hidden'
                        }}>
                            <thead>
                                <tr style={{ backgroundColor: theme?.colors?.primary || '#1976d2', color: theme?.colors?.surface || '#fff' }}>
                                    <th
                                        onClick={() => handleGroupSort('id')}
                                        style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}
                                    >
                                        ID {groupSortField === 'id' && (groupSortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        onClick={() => handleGroupSort('name')}
                                        style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}
                                    >
                                        {t('name')} {groupSortField === 'name' && (groupSortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        onClick={() => handleGroupSort('option_type_name')}
                                        style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}
                                    >
                                        {t('type')} {groupSortField === 'option_type_name' && (groupSortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        onClick={() => handleGroupSort('ordinal')}
                                        style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}
                                    >
                                        {t('ordinal')} {groupSortField === 'ordinal' && (groupSortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        onClick={() => handleGroupSort('mandatory')}
                                        style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}
                                    >
                                        {t('mandatory')} {groupSortField === 'mandatory' && (groupSortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        onClick={() => handleGroupSort('units')}
                                        style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}
                                    >
                                        {t('units')} {groupSortField === 'units' && (groupSortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th style={{ padding: '1rem', textAlign: 'center' }}>{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedOptionGroups.map((group, index) => (
                                    <tr key={group.id} style={{
                                        backgroundColor: index % 2 === 0 ? theme?.colors?.surface || '#fff' : theme?.colors?.background || '#f5f5f5',
                                        borderBottom: `1px solid ${theme?.colors?.border || '#ddd'}`
                                    }}>
                                        <td style={{ padding: '1rem' }}>{group.id}</td>
                                        <td style={{ padding: '1rem', fontWeight: 'bold' }}>{group.name}</td>
                                        <td style={{ padding: '1rem' }}>{group.option_type_name}</td>
                                        <td style={{ padding: '1rem', textAlign: 'center' }}>{group.ordinal}</td>
                                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                                            {group.mandatory ? t('yes') : t('no')}
                                        </td>
                                        <td style={{ padding: '1rem', textAlign: 'center' }}>{group.units || '-'}</td>
                                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                                            <button
                                                onClick={() => handleOpenEditGroup(group)}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    backgroundColor: theme?.colors?.primary || '#1976d2',
                                                    color: theme?.colors?.surface || '#fff',
                                                    border: 'none',
                                                    borderRadius: theme?.borderRadius || 4,
                                                    cursor: 'pointer',
                                                    marginRight: '0.5rem'
                                                }}
                                            >
                                                {t('edit')}
                                            </button>
                                            <button
                                                onClick={() => handleOpenAssign(group)}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    backgroundColor: theme?.colors?.success || '#4caf50',
                                                    color: theme?.colors?.surface || '#fff',
                                                    border: 'none',
                                                    borderRadius: theme?.borderRadius || 4,
                                                    cursor: 'pointer',
                                                    marginRight: '0.5rem'
                                                }}
                                            >
                                                {t('apply')}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteGroup(group.id)}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    backgroundColor: theme?.colors?.error || '#d32f2f',
                                                    color: theme?.colors?.surface || '#fff',
                                                    border: 'none',
                                                    borderRadius: theme?.borderRadius || 4,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {t('delete')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Options View */}
            {activeView === 'options' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h3 style={{ margin: 0, color: theme?.colors?.primary || '#1976d2' }}>
                            {t('options')} ({filteredOptions.length})
                        </h3>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <select
                                value={filterGroupId}
                                onChange={(e) => setFilterGroupId(e.target.value)}
                                style={{
                                    padding: '0.75rem',
                                    borderRadius: theme?.borderRadius || 4,
                                    border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                    backgroundColor: theme?.colors?.surface || '#fff',
                                    color: theme?.colors?.text || '#000',
                                    fontSize: '1rem'
                                }}
                            >
                                <option value="">{t('allGroups')}</option>
                                {optionGroups.map(group => (
                                    <option key={group.id} value={group.id}>{group.name}</option>
                                ))}
                            </select>
                            <button
                                onClick={() => setShowAddOptionForm(true)}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: theme?.colors?.primary || '#1976d2',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                + {t('add')} {t('option')}
                            </button>
                        </div>
                    </div>

                    {/* Search Input for Options */}
                    <div style={{ marginBottom: '1rem' }}>
                        <input
                            type="text"
                            value={searchTermOptions}
                            onChange={(e) => setSearchTermOptions(e.target.value)}
                            placeholder={t('searchOptions')}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                fontSize: '0.95rem',
                                border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                borderRadius: theme?.borderRadius || 4,
                                backgroundColor: theme?.colors?.surface || '#fff',
                                color: theme?.colors?.text || '#000',
                                outline: 'none'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = theme?.colors?.primary || '#1976d2';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = theme?.colors?.border || '#ddd';
                            }}
                        />
                        {searchTermOptions && (
                            <div style={{
                                marginTop: '0.5rem',
                                fontSize: '0.85rem',
                                color: theme?.colors?.textSecondary || '#666'
                            }}>
                                {t('showing')} {sortedOptions.length} {t('of')} {filteredOptions.length} {t('options').toLowerCase()}
                            </div>
                        )}
                    </div>

                    {/* Options Table */}
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            backgroundColor: theme?.colors?.surface || '#fff',
                            borderRadius: theme?.borderRadius || 8,
                            overflow: 'hidden'
                        }}>
                            <thead>
                                <tr style={{ backgroundColor: theme?.colors?.primary || '#1976d2', color: theme?.colors?.surface || '#fff' }}>
                                    <th
                                        onClick={() => handleOptionSort('id')}
                                        style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}
                                    >
                                        ID {optionSortField === 'id' && (optionSortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        onClick={() => handleOptionSort('name')}
                                        style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}
                                    >
                                        {t('name')} {optionSortField === 'name' && (optionSortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        onClick={() => handleOptionSort('option_group_name')}
                                        style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}
                                    >
                                        {t('optionGroup')} {optionSortField === 'option_group_name' && (optionSortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        onClick={() => handleOptionSort('ordinal')}
                                        style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}
                                    >
                                        {t('ordinal')} {optionSortField === 'ordinal' && (optionSortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        onClick={() => handleOptionSort('quantity')}
                                        style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}
                                    >
                                        {t('quantity')} {optionSortField === 'quantity' && (optionSortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        onClick={() => handleOptionSort('selected')}
                                        style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}
                                    >
                                        {t('selected')} {optionSortField === 'selected' && (optionSortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        onClick={() => handleOptionSort('price_adjust')}
                                        style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}
                                    >
                                        {t('priceAdjust')} {optionSortField === 'price_adjust' && (optionSortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th style={{ padding: '1rem', textAlign: 'center' }}>{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedOptions.map((option, index) => (
                                    <tr key={option.id} style={{
                                        backgroundColor: index % 2 === 0 ? theme?.colors?.surface || '#fff' : theme?.colors?.background || '#f5f5f5',
                                        borderBottom: `1px solid ${theme?.colors?.border || '#ddd'}`
                                    }}>
                                        <td style={{ padding: '1rem' }}>{option.id}</td>
                                        <td style={{ padding: '1rem', fontWeight: 'bold' }}>{option.name}</td>
                                        <td style={{ padding: '1rem' }}>{option.option_group_name}</td>
                                        <td style={{ padding: '1rem', textAlign: 'center' }}>{option.ordinal}</td>
                                        <td style={{ padding: '1rem', textAlign: 'center' }}>{option.quantity || '-'}</td>
                                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                                            {option.selected ? t('yes') : t('no')}
                                        </td>
                                        <td style={{ padding: '1rem', textAlign: 'center' }}>{option.price_adjust || 0}</td>
                                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                                            <button
                                                onClick={() => handleOpenEditOption(option)}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    backgroundColor: theme?.colors?.primary || '#1976d2',
                                                    color: theme?.colors?.surface || '#fff',
                                                    border: 'none',
                                                    borderRadius: theme?.borderRadius || 4,
                                                    cursor: 'pointer',
                                                    marginRight: '0.5rem'
                                                }}
                                            >
                                                {t('edit')}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteOption(option.id)}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    backgroundColor: theme?.colors?.error || '#d32f2f',
                                                    color: theme?.colors?.surface || '#fff',
                                                    border: 'none',
                                                    borderRadius: theme?.borderRadius || 4,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {t('delete')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Add/Edit Option Group Modal */}
            {(showAddGroupForm || editingGroup) && (
                <div onClick={() => {
                    setShowAddGroupForm(false);
                    setEditingGroup(null);
                    resetGroupForm();
                }} style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: theme?.colors?.surface || '#fff',
                        borderRadius: theme?.borderRadius || 8,
                        padding: '2rem',
                        maxWidth: '500px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <h3 style={{ color: theme?.colors?.primary || '#1976d2', marginTop: 0 }}>
                            {editingGroup ? t('edit') : t('add')} {t('optionGroup')}
                        </h3>

                        <form onSubmit={editingGroup ? handleEditGroup : handleAddGroup}>
                            {/* Top Button Section */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '1rem',
                                marginBottom: '1rem'
                            }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddGroupForm(false);
                                        setEditingGroup(null);
                                        resetGroupForm();
                                    }}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: theme?.colors?.error || '#f44336',
                                        color: theme?.colors?.surface || '#fff',
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 4,
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={!groupFormData.name || !groupFormData.option_type_id}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: (!groupFormData.name || !groupFormData.option_type_id) ? '#ccc' : (theme?.colors?.success || '#4caf50'),
                                        color: theme?.colors?.surface || '#fff',
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 4,
                                        cursor: (!groupFormData.name || !groupFormData.option_type_id) ? 'not-allowed' : 'pointer',
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        opacity: (!groupFormData.name || !groupFormData.option_type_id) ? 0.6 : 1
                                    }}
                                >
                                    {editingGroup ? t('update') : t('add')}
                                </button>
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                                    {t('name')}:*
                                </label>
                                <input
                                    type="text"
                                    value={groupFormData.name}
                                    onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                                    {t('type')}:*
                                </label>
                                <select
                                    value={groupFormData.option_type_id}
                                    onChange={(e) => setGroupFormData({ ...groupFormData, option_type_id: e.target.value })}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                        fontSize: '1rem'
                                    }}
                                >
                                    <option value="">{t('selectType')}</option>
                                    {optionTypes.map(type => (
                                        <option key={type.id} value={type.id}>{type.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                                    {t('ordinal')}:
                                </label>
                                <input
                                    type="number"
                                    value={getNumberInputValue(groupFormData.ordinal)}
                                    onChange={(e) => setGroupFormData({ ...groupFormData, ordinal: parseNumberInput(e.target.value, { min: 0 }) })}
                                    onWheel={(e) => e.target.blur()}
                                    placeholder="0"
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                                    {t('units')}:
                                </label>
                                <input
                                    type="text"
                                    value={groupFormData.units}
                                    onChange={(e) => setGroupFormData({ ...groupFormData, units: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    color: theme?.colors?.text || '#000'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={groupFormData.mandatory}
                                        onChange={(e) => setGroupFormData({ ...groupFormData, mandatory: e.target.checked })}
                                        style={{ marginRight: '0.5rem' }}
                                    />
                                    {t('mandatory')}
                                </label>
                            </div>

                            {/* Bottom Button Section */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '1rem'
                            }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddGroupForm(false);
                                        setEditingGroup(null);
                                        resetGroupForm();
                                    }}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: theme?.colors?.error || '#f44336',
                                        color: theme?.colors?.surface || '#fff',
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 4,
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={!groupFormData.name || !groupFormData.option_type_id}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: (!groupFormData.name || !groupFormData.option_type_id) ? '#ccc' : (theme?.colors?.success || '#4caf50'),
                                        color: theme?.colors?.surface || '#fff',
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 4,
                                        cursor: (!groupFormData.name || !groupFormData.option_type_id) ? 'not-allowed' : 'pointer',
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        opacity: (!groupFormData.name || !groupFormData.option_type_id) ? 0.6 : 1
                                    }}
                                >
                                    {editingGroup ? t('update') : t('add')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add/Edit Option Modal */}
            {(showAddOptionForm || editingOption) && (
                <div onClick={() => {
                    setShowAddOptionForm(false);
                    setEditingOption(null);
                    resetOptionForm();
                }} style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: theme?.colors?.surface || '#fff',
                        borderRadius: theme?.borderRadius || 8,
                        padding: '2rem',
                        maxWidth: '500px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <h3 style={{ color: theme?.colors?.primary || '#1976d2', marginTop: 0 }}>
                            {editingOption ? t('edit') : t('add')} {t('option')}
                        </h3>

                        <form onSubmit={editingOption ? handleEditOption : handleAddOption}>
                            {/* Top Button Section */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '1rem',
                                marginBottom: '1rem'
                            }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddOptionForm(false);
                                        setEditingOption(null);
                                        resetOptionForm();
                                    }}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: theme?.colors?.error || '#f44336',
                                        color: theme?.colors?.surface || '#fff',
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 4,
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={!optionFormData.name || !optionFormData.option_group_id}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: (!optionFormData.name || !optionFormData.option_group_id) ? '#ccc' : (theme?.colors?.success || '#4caf50'),
                                        color: theme?.colors?.surface || '#fff',
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 4,
                                        cursor: (!optionFormData.name || !optionFormData.option_group_id) ? 'not-allowed' : 'pointer',
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        opacity: (!optionFormData.name || !optionFormData.option_group_id) ? 0.6 : 1
                                    }}
                                >
                                    {editingOption ? t('update') : t('add')}
                                </button>
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                                    {t('name')}:*
                                </label>
                                <input
                                    type="text"
                                    value={optionFormData.name}
                                    onChange={(e) => setOptionFormData({ ...optionFormData, name: e.target.value })}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                                    {t('optionGroup')}:*
                                </label>
                                <select
                                    value={optionFormData.option_group_id}
                                    onChange={(e) => setOptionFormData({ ...optionFormData, option_group_id: e.target.value })}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                        fontSize: '1rem'
                                    }}
                                >
                                    <option value="">{t('selectOptionGroup')}</option>
                                    {optionGroups.map(group => (
                                        <option key={group.id} value={group.id}>{group.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                                    {t('ordinal')}:
                                </label>
                                <input
                                    type="number"
                                    value={getNumberInputValue(optionFormData.ordinal)}
                                    onChange={(e) => setOptionFormData({ ...optionFormData, ordinal: parseNumberInput(e.target.value, { min: 0 }) })}
                                    onWheel={(e) => e.target.blur()}
                                    placeholder="0"
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                                    {t('quantity')}:
                                </label>
                                <input
                                    type="number"
                                    value={getNumberInputValue(optionFormData.quantity)}
                                    onChange={(e) => setOptionFormData({ ...optionFormData, quantity: parseNumberInput(e.target.value, { min: 0 }) })}
                                    onWheel={(e) => e.target.blur()}
                                    placeholder="0"
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                                    {t('priceAdjust')}:
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={getNumberInputValue(optionFormData.price_adjust)}
                                    onChange={(e) => setOptionFormData({ ...optionFormData, price_adjust: parseNumberInput(e.target.value, { allowDecimals: true, min: -Infinity }) })}
                                    placeholder="0.00"
                                    onWheel={(e) => e.target.blur()}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    color: theme?.colors?.text || '#000'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={optionFormData.selected}
                                        onChange={(e) => setOptionFormData({ ...optionFormData, selected: e.target.checked })}
                                        style={{ marginRight: '0.5rem' }}
                                    />
                                    {t('selectedByDefault')}
                                </label>
                            </div>

                            {/* Bottom Button Section */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '1rem'
                            }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddOptionForm(false);
                                        setEditingOption(null);
                                        resetOptionForm();
                                    }}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: theme?.colors?.error || '#f44336',
                                        color: theme?.colors?.surface || '#fff',
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 4,
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={!optionFormData.name || !optionFormData.option_group_id}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: (!optionFormData.name || !optionFormData.option_group_id) ? '#ccc' : (theme?.colors?.success || '#4caf50'),
                                        color: theme?.colors?.surface || '#fff',
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 4,
                                        cursor: (!optionFormData.name || !optionFormData.option_group_id) ? 'not-allowed' : 'pointer',
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        opacity: (!optionFormData.name || !optionFormData.option_group_id) ? 0.6 : 1
                                    }}
                                >
                                    {editingOption ? t('update') : t('add')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Bulk Assign Modal */}
            {assigningGroup && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div style={{
                        backgroundColor: theme?.colors?.surface || '#fff',
                        color: theme?.colors?.text || '#000',
                        borderRadius: theme?.borderRadius || 8,
                        padding: '1.5rem',
                        maxWidth: '600px', width: '90%', maxHeight: '80vh',
                        display: 'flex', flexDirection: 'column'
                    }}>
                        <h3 style={{ margin: '0 0 0.75rem 0', color: theme?.colors?.primary }}>
                            {t('apply')} "{assigningGroup.name}" {t('to')} {t('products')}
                        </h3>

                        {/* Category filter */}
                        <select
                            value={assignFilterCategory}
                            onChange={(e) => setAssignFilterCategory(e.target.value)}
                            style={{
                                padding: '0.4rem', marginBottom: '0.75rem',
                                borderRadius: theme?.borderRadius || 4,
                                border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                                backgroundColor: theme?.colors?.background || '#f5f5f5',
                                color: theme?.colors?.text || '#000'
                            }}
                        >
                            <option value="">{t('allCategories')}</option>
                            {allCategories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>

                        {assignLoading ? (
                            <div style={{ padding: '2rem', textAlign: 'center' }}>{t('loading')}...</div>
                        ) : (
                            <div style={{ overflowY: 'auto', flex: 1 }}>
                                {/* Group products by category */}
                                {allCategories
                                    .filter(cat => !assignFilterCategory || cat.id === parseInt(assignFilterCategory))
                                    .map(cat => {
                                        const catProducts = allProducts.filter(p => p.category_id === cat.id);
                                        if (catProducts.length === 0) return null;
                                        const allSelected = catProducts.every(p => selectedProductIds.includes(p.id));
                                        const someSelected = catProducts.some(p => selectedProductIds.includes(p.id));
                                        return (
                                            <div key={cat.id} style={{ marginBottom: '0.75rem' }}>
                                                {/* Category header with select-all */}
                                                <label style={{
                                                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                                                    padding: '0.4rem 0.5rem', cursor: 'pointer',
                                                    backgroundColor: theme?.colors?.primary || '#1976d2',
                                                    color: theme?.colors?.surface || '#fff',
                                                    borderRadius: theme?.borderRadius || 4,
                                                    fontWeight: 'bold', fontSize: '0.85rem'
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={allSelected}
                                                        ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                                                        onChange={() => toggleAllInCategory(cat.id)}
                                                        style={{ width: '16px', height: '16px' }}
                                                    />
                                                    {cat.name} ({catProducts.filter(p => selectedProductIds.includes(p.id)).length}/{catProducts.length})
                                                </label>
                                                {/* Product checkboxes */}
                                                <div style={{ padding: '0.25rem 0 0 1.5rem' }}>
                                                    {catProducts.map(product => (
                                                        <label key={product.id} style={{
                                                            display: 'flex', alignItems: 'center', gap: '0.4rem',
                                                            padding: '0.2rem 0', cursor: 'pointer', fontSize: '0.85rem',
                                                            color: theme?.colors?.text || '#000'
                                                        }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedProductIds.includes(product.id)}
                                                                onChange={() => toggleProductSelection(product.id)}
                                                                style={{ width: '15px', height: '15px' }}
                                                            />
                                                            {product.name}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        )}

                        {/* Footer */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: `1px solid ${theme?.colors?.border || '#ccc'}` }}>
                            <span style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
                                {selectedProductIds.length} {t('products')} {t('selected') || 'selected'}
                            </span>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                    onClick={() => setAssigningGroup(null)}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        backgroundColor: theme?.colors?.error || '#d32f2f',
                                        color: theme?.colors?.surface || '#fff',
                                        border: 'none', borderRadius: theme?.borderRadius || 4,
                                        cursor: 'pointer', fontWeight: 'bold'
                                    }}
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    onClick={handleSaveAssign}
                                    disabled={assignLoading}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        backgroundColor: theme?.colors?.success || '#4caf50',
                                        color: theme?.colors?.surface || '#fff',
                                        border: 'none', borderRadius: theme?.borderRadius || 4,
                                        cursor: assignLoading ? 'not-allowed' : 'pointer',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {assignLoading ? '...' : t('save')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
